import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";
import { CoachingStatusCard } from "@/components/CoachingStatusCard";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let tries = 0;
    let stop = false;
    const poll = async () => {
      if (stop) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          const s = await api.get("/billing/status");
          setStatus(s.data);
          setState("paid");
          return;
        }
        if (data.payment_status === "duplicate_refunded" || data.payment_status === "duplicate") {
          setState("duplicate");
          return;
        }
        if (["expired", "failed", "refunded"].includes(data.payment_status)) {
          setState("error");
          return;
        }
      } catch {}
      tries += 1;
      if (tries >= 15) setState("timeout");
      else setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#FDFBF7] grain">
      <AppHeader />
      <main className="max-w-xl mx-auto px-6 py-16 text-center">
        {state === "checking" && (
          <div data-testid="payment-checking" className="text-[#5C6A61]">
            <Loader2 className="mx-auto animate-spin mb-4" size={28} />
            Confirming your payment with Stripe…
          </div>
        )}
        {state === "paid" && (
          <div data-testid="payment-success" className="space-y-6">
            <CheckCircle2 className="mx-auto text-[#81B29A]" size={44} />
            <h1 className="font-serif-display text-4xl text-[#1C221D]">Welcome to the Reset, baby.</h1>
            <p className="text-[#5C6A61]">Your payment is confirmed. Your four weeks start today.</p>
            <div className="text-left"><CoachingStatusCard status={status} /></div>
            <Link to="/program" data-testid="go-to-program" className="inline-block px-8 py-3.5 rounded-full bg-[#2A3B32] text-[#FDFBF7] hover:-translate-y-0.5 transition-transform">
              Start Week 1
            </Link>
          </div>
        )}
        {state === "duplicate" && (
          <div data-testid="payment-duplicate" className="space-y-4">
            <CheckCircle2 className="mx-auto text-[#81B29A]" size={40} />
            <h1 className="font-serif-display text-3xl text-[#1C221D]">You already own the Reset, baby.</h1>
            <p className="text-[#5C6A61]">
              This second payment was not needed, so it has been refunded automatically. Refunds usually show on your
              statement within 5–10 business days. Your original purchase and access are unchanged.
            </p>
            <Link to="/program" data-testid="duplicate-go-to-program" className="inline-block px-8 py-3.5 rounded-full bg-[#2A3B32] text-[#FDFBF7]">
              Go to your program
            </Link>
          </div>
        )}
        {(state === "error" || state === "timeout") && (
          <div data-testid="payment-not-confirmed" className="space-y-4">
            <XCircle className="mx-auto text-[#E07A5F]" size={40} />
            <h1 className="font-serif-display text-3xl text-[#1C221D]">
              {state === "timeout" ? "Still confirming your payment" : "Payment not completed"}
            </h1>
            <p className="text-[#5C6A61]">
              {state === "timeout"
                ? "This can take a minute. Refresh this page shortly. You won't be charged twice."
                : "No charge was made. You can try again whenever you're ready."}
            </p>
            <Link to="/program" className="inline-block text-[#2A3B32] underline underline-offset-4">Back to the program</Link>
          </div>
        )}
      </main>
    </div>
  );
}
